/**
 * /water — dedicated hydration page with the WaterCard hero + a 7-day chart
 * showing daily totals and which days hit goal.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Droplet, CheckCircle2 } from 'lucide-react';
import { WaterCard } from '../components/water/WaterCard';
import { useAuth } from '../context/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const WaterPage = () => {
  const { token } = useAuth();
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${BACKEND}/api/water-log/week`, {
      withCredentials: true,
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    })
      .then((res) => { if (alive) setWeek(res.data); })
      .catch(() => { if (alive) setWeek(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const maxOz = Math.max(week?.goal_oz || 64, ...(week?.points || []).map((p) => p.ounces), 1);

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="water-page">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <header className="mb-5">
          <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-wider flex items-center gap-3">
            Water <span className="text-blue-400">Tracker</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            Hit your daily goal. Small sips, big difference.
          </p>
        </header>

        <WaterCard />

        <section className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-sm text-white uppercase tracking-wider">Last 7 Days</h2>
            {week && <span className="text-[11px] text-muted-foreground">Goal: {Math.round(week.goal_oz)} oz/day</span>}
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-400" /></div>
          ) : !week ? (
            <p className="text-muted-foreground text-sm italic">Couldn't load history.</p>
          ) : (
            <div className="bg-card border border-border rounded-2xl p-4">
              <div className="flex items-end gap-2 h-40">
                {week.points.map((p) => {
                  const h = (p.ounces / maxOz) * 100;
                  const dayLabel = new Date(p.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' });
                  return (
                    <div key={p.date} className="flex-1 flex flex-col items-center justify-end gap-1" data-testid={`water-week-bar-${p.date}`}>
                      <div className="text-[10px] text-muted-foreground">{Math.round(p.ounces)}</div>
                      <div className="w-full bg-black/40 rounded-md relative overflow-hidden" style={{ height: '100%' }}>
                        <div
                          className={`absolute bottom-0 left-0 right-0 transition-[height] duration-500 ${p.hit_goal ? 'bg-green-400' : 'bg-blue-400'}`}
                          style={{ height: `${Math.min(100, h)}%` }}
                        />
                        {/* Goal line */}
                        <div
                          className="absolute left-0 right-0 border-t border-dashed border-yellow-400/60"
                          style={{ bottom: `${Math.min(100, (week.goal_oz / maxOz) * 100)}%` }}
                        />
                      </div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        {p.hit_goal && <CheckCircle2 className="h-3 w-3 text-green-400" />}
                        {dayLabel}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground mt-3">
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-400" /> Goal hit</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-400" /> Below goal</span>
                <span className="flex items-center gap-1"><span className="h-0.5 w-3 border-t border-dashed border-yellow-400" /> Daily goal</span>
              </div>
            </div>
          )}
        </section>

        <p className="text-center text-muted-foreground text-xs mt-6 flex items-center justify-center gap-1.5">
          <Droplet className="h-3 w-3 text-blue-400" /> Suggested goal: ½ oz per pound of body weight.
        </p>
      </main>
    </div>
  );
};

export default WaterPage;
